/**
 * Move generation - jumping pieces (knight, king), sliding pieces (rook, bishop, queen), and pawns
 */

import type { Move, GameState, Square } from './types';
import { Color, PieceType } from './types';
import { getRank, getFile, getPositionHash } from './board';

/**
 * Knight move offsets: L-shaped moves (2 squares in one direction, 1 in perpendicular)
 */
const KNIGHT_OFFSETS: [number, number][] = [
  [2, 1], [2, -1], [-2, 1], [-2, -1],
  [1, 2], [1, -2], [-1, 2], [-1, -2],
];

/**
 * King move offsets: 1 square in any direction
 */
const KING_OFFSETS: [number, number][] = [
  [1, 0], [1, 1], [0, 1], [-1, 1],
  [-1, 0], [-1, -1], [0, -1], [1, -1],
];

/**
 * Rook direction vectors: horizontal and vertical
 */
const ROOK_DIRECTIONS: [number, number][] = [
  [1, 0],   // North
  [-1, 0],  // South
  [0, 1],   // East
  [0, -1],  // West
];

/**
 * Bishop direction vectors: diagonals
 */
const BISHOP_DIRECTIONS: [number, number][] = [
  [1, 1],   // Northeast
  [1, -1],  // Northwest
  [-1, 1],  // Southeast
  [-1, -1], // Southwest
];

/**
 * Queen direction vectors: all 8 directions (rook + bishop)
 */
const QUEEN_DIRECTIONS: [number, number][] = [
  ...ROOK_DIRECTIONS,
  ...BISHOP_DIRECTIONS,
];

/**
 * Generates moves for a knight at the given square
 */
export function generateKnightMoves(state: GameState, fromSquare: Square): Move[] {
  return generateJumpingMoves(state, fromSquare, KNIGHT_OFFSETS);
}

/**
 * Generates moves for a king at the given square (including castling)
 */
export function generateKingMoves(state: GameState, fromSquare: Square): Move[] {
  const moves = generateJumpingMoves(state, fromSquare, KING_OFFSETS);
  const piece = state.board[fromSquare];

  if (!piece) {
    return moves;
  }

  // Add castling moves
  const castlingMoves = generateCastlingMoves(state, fromSquare, piece.color);
  moves.push(...castlingMoves);

  return moves;
}

/**
 * Generates moves for a rook at the given square
 */
export function generateRookMoves(state: GameState, fromSquare: Square): Move[] {
  return generateSlidingMoves(state, fromSquare, ROOK_DIRECTIONS);
}

/**
 * Generates moves for a bishop at the given square
 */
export function generateBishopMoves(state: GameState, fromSquare: Square): Move[] {
  return generateSlidingMoves(state, fromSquare, BISHOP_DIRECTIONS);
}

/**
 * Generates moves for a queen at the given square
 */
export function generateQueenMoves(state: GameState, fromSquare: Square): Move[] {
  return generateSlidingMoves(state, fromSquare, QUEEN_DIRECTIONS);
}

/**
 * Generates moves for a pawn at the given square
 * Includes forward moves, captures, en passant, and promotion
 */
export function generatePawnMoves(state: GameState, fromSquare: Square): Move[] {
  const moves: Move[] = [];
  const piece = state.board[fromSquare];

  if (!piece) {
    return moves;
  }

  const fromRank = getRank(fromSquare);
  const fromFile = getFile(fromSquare);

  // Direction: +1 for white (moves up), -1 for black (moves down)
  const direction = piece.color === Color.White ? 1 : -1;
  // Starting rank: 1 for white, 6 for black
  const startRank = piece.color === Color.White ? 1 : 6;
  // Promotion rank: 7 for white, 0 for black
  const promotionRank = piece.color === Color.White ? 7 : 0;

  // Forward move (1 square)
  const oneSquareRank = fromRank + direction;
  if (oneSquareRank >= 0 && oneSquareRank < 8) {
    const oneSquareTarget = oneSquareRank * 8 + fromFile;

    // Can only move forward if square is empty
    if (!state.board[oneSquareTarget]) {
      // Check if this move reaches promotion rank
      if (oneSquareRank === promotionRank) {
        // Generate promotion moves for all piece types
        for (const promotionPiece of [PieceType.Queen, PieceType.Rook, PieceType.Bishop, PieceType.Knight]) {
          moves.push({
            from: fromSquare,
            to: oneSquareTarget,
            piece,
            captured: undefined,
            promotion: promotionPiece,
          });
        }
      } else {
        moves.push({
          from: fromSquare,
          to: oneSquareTarget,
          piece,
          captured: undefined,
        });

        // Forward move (2 squares from starting position)
        if (fromRank === startRank) {
          const twoSquareRank = fromRank + 2 * direction;
          const twoSquareTarget = twoSquareRank * 8 + fromFile;

          // Both squares must be empty
          if (!state.board[twoSquareTarget]) {
            moves.push({
              from: fromSquare,
              to: twoSquareTarget,
              piece,
              captured: undefined,
            });
          }
        }
      }
    }
  }

  // Diagonal captures (left and right)
  for (const fileDelta of [-1, 1]) {
    const captureRank = fromRank + direction;
    const captureFile = fromFile + fileDelta;

    // Check bounds
    if (captureRank >= 0 && captureRank < 8 && captureFile >= 0 && captureFile < 8) {
      const captureSquare = captureRank * 8 + captureFile;
      const targetPiece = state.board[captureSquare];

      // Regular capture: enemy piece on diagonal
      if (targetPiece && targetPiece.color !== piece.color) {
        // Check if this capture reaches promotion rank
        if (captureRank === promotionRank) {
          // Generate promotion moves for all piece types
          for (const promotionPiece of [PieceType.Queen, PieceType.Rook, PieceType.Bishop, PieceType.Knight]) {
            moves.push({
              from: fromSquare,
              to: captureSquare,
              piece,
              captured: targetPiece,
              promotion: promotionPiece,
            });
          }
        } else {
          moves.push({
            from: fromSquare,
            to: captureSquare,
            piece,
            captured: targetPiece,
          });
        }
      }

      // En passant capture
      if (state.enPassantSquare === captureSquare) {
        // The captured pawn is actually one rank behind the target square
        const capturedPawnRank = captureRank - direction;
        const capturedPawnSquare = capturedPawnRank * 8 + captureFile;
        const capturedPawn = state.board[capturedPawnSquare];

        moves.push({
          from: fromSquare,
          to: captureSquare,
          piece,
          captured: capturedPawn ?? undefined,
          isEnPassant: true,
        });
      }
    }
  }

  return moves;
}

/**
 * Generates castling moves for a king at the given square
 * @param state - Current game state
 * @param fromSquare - King's current square
 * @param color - King's color
 */
function generateCastlingMoves(state: GameState, fromSquare: Square, color: Color): Move[] {
  const moves: Move[] = [];
  const piece = state.board[fromSquare];

  if (!piece) {
    return moves;
  }

  const fromRank = getRank(fromSquare);
  const fromFile = getFile(fromSquare);

  // White king should be on rank 0 (e1 = square 4)
  // Black king should be on rank 7 (e8 = square 60)
  const expectedRank = color === Color.White ? 0 : 7;
  const expectedFile = 4; // e-file

  // King must be in starting position
  if (fromRank !== expectedRank || fromFile !== expectedFile) {
    return moves;
  }

  // TODO (Phase 2): Check if king is currently in check - cannot castle out of check
  // if (isSquareAttacked(state, fromSquare, color === Color.White ? Color.Black : Color.White)) {
  //   return moves;
  // }

  if (color === Color.White) {
    // White kingside castling (O-O): e1 → g1
    if (state.castlingRights.whiteKingside) {
      const f1 = 5;  // f1
      const g1 = 6;  // g1

      // Check if squares between king and rook are empty
      if (!state.board[f1] && !state.board[g1]) {
        // TODO (Phase 2): Check if king passes through attacked squares
        // if (!isSquareAttacked(state, f1, Color.Black) && !isSquareAttacked(state, g1, Color.Black)) {
        moves.push({
          from: fromSquare,
          to: g1,
          piece,
          captured: undefined,
          isCastling: true,
        });
        // }
      }
    }

    // White queenside castling (O-O-O): e1 → c1
    if (state.castlingRights.whiteQueenside) {
      const d1 = 3;  // d1
      const c1 = 2;  // c1
      const b1 = 1;  // b1

      // Check if squares between king and rook are empty
      if (!state.board[d1] && !state.board[c1] && !state.board[b1]) {
        // TODO (Phase 2): Check if king passes through attacked squares
        // Note: b1 doesn't need to be checked for attacks
        // if (!isSquareAttacked(state, d1, Color.Black) && !isSquareAttacked(state, c1, Color.Black)) {
        moves.push({
          from: fromSquare,
          to: c1,
          piece,
          captured: undefined,
          isCastling: true,
        });
        // }
      }
    }
  } else {
    // Black kingside castling (O-O): e8 → g8
    if (state.castlingRights.blackKingside) {
      const f8 = 61;  // f8
      const g8 = 62;  // g8

      // Check if squares between king and rook are empty
      if (!state.board[f8] && !state.board[g8]) {
        // TODO (Phase 2): Check if king passes through attacked squares
        // if (!isSquareAttacked(state, f8, Color.White) && !isSquareAttacked(state, g8, Color.White)) {
        moves.push({
          from: fromSquare,
          to: g8,
          piece,
          captured: undefined,
          isCastling: true,
        });
        // }
      }
    }

    // Black queenside castling (O-O-O): e8 → c8
    if (state.castlingRights.blackQueenside) {
      const d8 = 59;  // d8
      const c8 = 58;  // c8
      const b8 = 57;  // b8

      // Check if squares between king and rook are empty
      if (!state.board[d8] && !state.board[c8] && !state.board[b8]) {
        // TODO (Phase 2): Check if king passes through attacked squares
        // Note: b8 doesn't need to be checked for attacks
        // if (!isSquareAttacked(state, d8, Color.White) && !isSquareAttacked(state, c8, Color.White)) {
        moves.push({
          from: fromSquare,
          to: c8,
          piece,
          captured: undefined,
          isCastling: true,
        });
        // }
      }
    }
  }

  return moves;
}

/**
 * Generic function to generate jumping piece moves
 * @param state - Current game state
 * @param fromSquare - Source square
 * @param offsets - Array of [rankDelta, fileDelta] offsets
 */
function generateJumpingMoves(
  state: GameState,
  fromSquare: Square,
  offsets: [number, number][]
): Move[] {
  const moves: Move[] = [];
  const piece = state.board[fromSquare];

  if (!piece) {
    return moves;
  }

  const fromRank = getRank(fromSquare);
  const fromFile = getFile(fromSquare);

  for (const [rankDelta, fileDelta] of offsets) {
    const toRank = fromRank + rankDelta;
    const toFile = fromFile + fileDelta;

    // Check if destination is on the board
    if (toRank < 0 || toRank >= 8 || toFile < 0 || toFile >= 8) {
      continue;
    }

    const toSquare = toRank * 8 + toFile;
    const targetPiece = state.board[toSquare];

    // Skip if destination has a friendly piece
    if (targetPiece && targetPiece.color === piece.color) {
      continue;
    }

    // Valid move - either empty square or enemy piece (capture)
    moves.push({
      from: fromSquare,
      to: toSquare,
      piece,
      captured: targetPiece ?? undefined,
    });
  }

  return moves;
}

/**
 * Generic function to generate sliding piece moves
 * @param state - Current game state
 * @param fromSquare - Source square
 * @param directions - Array of [rankDelta, fileDelta] direction vectors
 */
function generateSlidingMoves(
  state: GameState,
  fromSquare: Square,
  directions: [number, number][]
): Move[] {
  const moves: Move[] = [];
  const piece = state.board[fromSquare];

  if (!piece) {
    return moves;
  }

  const fromRank = getRank(fromSquare);
  const fromFile = getFile(fromSquare);

  // For each direction, slide until we hit something or the board edge
  for (const [rankDelta, fileDelta] of directions) {
    let currentRank = fromRank;
    let currentFile = fromFile;

    while (true) {
      // Move one step in this direction
      currentRank += rankDelta;
      currentFile += fileDelta;

      // Check if we've gone off the board
      if (currentRank < 0 || currentRank >= 8 || currentFile < 0 || currentFile >= 8) {
        break;
      }

      const toSquare = currentRank * 8 + currentFile;
      const targetPiece = state.board[toSquare];

      // If there's a piece at this square
      if (targetPiece) {
        // If it's an enemy piece, we can capture it, then stop
        if (targetPiece.color !== piece.color) {
          moves.push({
            from: fromSquare,
            to: toSquare,
            piece,
            captured: targetPiece,
          });
        }
        // If it's a friendly piece or we just captured, stop sliding in this direction
        break;
      }

      // Empty square - add the move and continue sliding
      moves.push({
        from: fromSquare,
        to: toSquare,
        piece,
        captured: undefined,
      });
    }
  }

  return moves;
}

/**
 * Finds the square where the king of the given color is located
 * @param state - Current game state
 * @param color - Color of the king to find
 * @returns The square where the king is located, or null if not found
 */
export function findKingSquare(state: GameState, color: Color): Square | null {
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (piece && piece.color === color && piece.type === PieceType.King) {
      return square;
    }
  }
  return null;
}

/**
 * Checks if the given color's king is currently in check
 * @param state - Current game state
 * @param color - Color of the king to check
 * @returns true if the king is in check, false otherwise
 */
export function isInCheck(state: GameState, color: Color): boolean {
  const kingSquare = findKingSquare(state, color);
  if (kingSquare === null) {
    return false; // No king found (shouldn't happen in valid positions)
  }
  
  // Check if the king's square is attacked by the opponent
  const opponentColor = color === Color.White ? Color.Black : Color.White;
  return isSquareAttacked(state, kingSquare, opponentColor);
}

/**
 * Generates all legal moves for the current position
 * Filters out pseudo-legal moves that would leave the king in check
 */
export function generateLegalMoves(state: GameState): Move[] {
  const pseudoLegalMoves = generatePseudoLegalMoves(state);
  const legalMoves: Move[] = [];
  
  for (const move of pseudoLegalMoves) {
    if (isMoveLegal(state, move)) {
      legalMoves.push(move);
    }
  }
  
  return legalMoves;
}

/**
 * Generates all pseudo-legal moves (may leave king in check)
 * Iterates through all pieces of the active color and generates their moves
 */
export function generatePseudoLegalMoves(state: GameState): Move[] {
  const moves: Move[] = [];

  // Iterate through all squares on the board
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];

    // Skip empty squares or opponent's pieces
    if (!piece || piece.color !== state.activeColor) {
      continue;
    }

    // Generate moves based on piece type
    let pieceMoves: Move[] = [];
    switch (piece.type) {
      case 0: // PieceType.Pawn
        pieceMoves = generatePawnMoves(state, square);
        break;
      case 1: // PieceType.Knight
        pieceMoves = generateKnightMoves(state, square);
        break;
      case 2: // PieceType.Bishop
        pieceMoves = generateBishopMoves(state, square);
        break;
      case 3: // PieceType.Rook
        pieceMoves = generateRookMoves(state, square);
        break;
      case 4: // PieceType.Queen
        pieceMoves = generateQueenMoves(state, square);
        break;
      case 5: // PieceType.King
        pieceMoves = generateKingMoves(state, square);
        break;
    }

    moves.push(...pieceMoves);
  }

  return moves;
}

/**
 * Checks if a move is legal (doesn't leave own king in check)
 * @param state - Current game state
 * @param move - Move to check
 * @returns true if the move is legal, false otherwise
 */
export function isMoveLegal(state: GameState, move: Move): boolean {
  // Make the move temporarily
  const newState = makeMove(state, move);
  
  // Check if the move leaves our own king in check
  // Note: activeColor in newState has already been toggled, so we check the opposite color
  const ourColor = state.activeColor;
  const inCheck = isInCheck(newState, ourColor);
  
  // Move is legal if it doesn't leave our king in check
  return !inCheck;
}

/**
 * Makes a move on the board (returns new state)
 * This creates a new GameState with the move applied
 * @param state - Current game state
 * @param move - Move to make
 * @returns New game state with the move applied
 */
export function makeMove(state: GameState, move: Move): GameState {
  // Create a deep copy of the board
  const newBoard = state.board.map(piece => piece ? { ...piece } : null);
  
  // Make the basic move
  newBoard[move.to] = newBoard[move.from];
  newBoard[move.from] = null;
  
  // Handle special moves
  
  // Promotion: replace pawn with promoted piece
  if (move.promotion !== undefined && newBoard[move.to]) {
    newBoard[move.to] = {
      type: move.promotion,
      color: move.piece.color,
    };
  }
  
  // En passant: remove the captured pawn
  if (move.isEnPassant && move.captured) {
    const direction = move.piece.color === Color.White ? 1 : -1;
    const capturedPawnRank = getRank(move.to) - direction;
    const capturedPawnSquare = capturedPawnRank * 8 + getFile(move.to);
    newBoard[capturedPawnSquare] = null;
  }
  
  // Castling: move the rook
  if (move.isCastling) {
    const rank = getRank(move.from);
    const fromFile = getFile(move.from);
    const toFile = getFile(move.to);
    
    // Determine which side we're castling to
    if (toFile > fromFile) {
      // Kingside castling: rook moves from h-file to f-file
      const rookFrom = rank * 8 + 7; // h-file
      const rookTo = rank * 8 + 5;   // f-file
      newBoard[rookTo] = newBoard[rookFrom];
      newBoard[rookFrom] = null;
    } else {
      // Queenside castling: rook moves from a-file to d-file
      const rookFrom = rank * 8 + 0; // a-file
      const rookTo = rank * 8 + 3;   // d-file
      newBoard[rookTo] = newBoard[rookFrom];
      newBoard[rookFrom] = null;
    }
  }
  
  // Update castling rights
  const newCastlingRights = { ...state.castlingRights };
  
  // If king moves, lose all castling rights for that color
  if (move.piece.type === PieceType.King) {
    if (move.piece.color === Color.White) {
      newCastlingRights.whiteKingside = false;
      newCastlingRights.whiteQueenside = false;
    } else {
      newCastlingRights.blackKingside = false;
      newCastlingRights.blackQueenside = false;
    }
  }
  
  // If rook moves from starting position, lose castling rights for that side
  if (move.piece.type === PieceType.Rook) {
    if (move.piece.color === Color.White) {
      if (move.from === 0) { // a1
        newCastlingRights.whiteQueenside = false;
      } else if (move.from === 7) { // h1
        newCastlingRights.whiteKingside = false;
      }
    } else {
      if (move.from === 56) { // a8
        newCastlingRights.blackQueenside = false;
      } else if (move.from === 63) { // h8
        newCastlingRights.blackKingside = false;
      }
    }
  }
  
  // If rook is captured, lose castling rights for that side
  if (move.captured && move.captured.type === PieceType.Rook) {
    if (move.captured.color === Color.White) {
      if (move.to === 0) { // a1
        newCastlingRights.whiteQueenside = false;
      } else if (move.to === 7) { // h1
        newCastlingRights.whiteKingside = false;
      }
    } else {
      if (move.to === 56) { // a8
        newCastlingRights.blackQueenside = false;
      } else if (move.to === 63) { // h8
        newCastlingRights.blackKingside = false;
      }
    }
  }
  
  // Update en passant square
  let newEnPassantSquare: Square | null = null;
  
  // If pawn moved two squares, set en passant square
  if (move.piece.type === PieceType.Pawn) {
    const fromRank = getRank(move.from);
    const toRank = getRank(move.to);
    const rankDiff = Math.abs(toRank - fromRank);
    
    if (rankDiff === 2) {
      // En passant square is the square the pawn skipped over
      const enPassantRank = (fromRank + toRank) / 2;
      newEnPassantSquare = enPassantRank * 8 + getFile(move.from);
    }
  }
  
  // Toggle active color
  const newActiveColor = state.activeColor === Color.White ? Color.Black : Color.White;
  
  // Update halfmove clock (for 50-move rule)
  let newHalfmoveClock = state.halfmoveClock + 1;
  if (move.piece.type === PieceType.Pawn || move.captured) {
    newHalfmoveClock = 0; // Reset on pawn move or capture
  }
  
  // Update fullmove number
  let newFullmoveNumber = state.fullmoveNumber;
  if (state.activeColor === Color.Black) {
    newFullmoveNumber++; // Increment after black's move
  }
  
  // Create new state with updated position history
  const newState: GameState = {
    board: newBoard,
    activeColor: newActiveColor,
    castlingRights: newCastlingRights,
    enPassantSquare: newEnPassantSquare,
    halfmoveClock: newHalfmoveClock,
    fullmoveNumber: newFullmoveNumber,
    positionHistory: [...state.positionHistory], // Copy existing history
  };
  
  // Add new position to history
  newState.positionHistory.push(getPositionHash(newState));
  
  return newState;
}

/**
 * Checks if a square is attacked by a given color
 * Used for king safety validation and castling legality
 * @param state - Current game state
 * @param square - The square to check
 * @param byColor - The color of the attacking pieces
 * @returns true if the square is attacked by any piece of the given color
 */
export function isSquareAttacked(state: GameState, square: Square, byColor: Color): boolean {
  const rank = getRank(square);
  const file = getFile(square);

  // Check for pawn attacks
  // Pawns attack diagonally forward, so we check one rank in the opposite direction
  const pawnDirection = byColor === Color.White ? 1 : -1; // White pawns move up, black down
  for (const fileDelta of [-1, 1]) {
    const pawnRank = rank - pawnDirection; // Reverse to find attacking pawn position
    const pawnFile = file + fileDelta;
    
    if (pawnRank >= 0 && pawnRank < 8 && pawnFile >= 0 && pawnFile < 8) {
      const pawnSquare = pawnRank * 8 + pawnFile;
      const piece = state.board[pawnSquare];
      if (piece && piece.color === byColor && piece.type === PieceType.Pawn) {
        return true;
      }
    }
  }

  // Check for knight attacks
  for (const [rankDelta, fileDelta] of KNIGHT_OFFSETS) {
    const knightRank = rank + rankDelta;
    const knightFile = file + fileDelta;
    
    if (knightRank >= 0 && knightRank < 8 && knightFile >= 0 && knightFile < 8) {
      const knightSquare = knightRank * 8 + knightFile;
      const piece = state.board[knightSquare];
      if (piece && piece.color === byColor && piece.type === PieceType.Knight) {
        return true;
      }
    }
  }

  // Check for bishop/queen diagonal attacks
  for (const [rankDelta, fileDelta] of BISHOP_DIRECTIONS) {
    let currentRank = rank;
    let currentFile = file;
    
    while (true) {
      currentRank += rankDelta;
      currentFile += fileDelta;
      
      if (currentRank < 0 || currentRank >= 8 || currentFile < 0 || currentFile >= 8) {
        break;
      }
      
      const targetSquare = currentRank * 8 + currentFile;
      const piece = state.board[targetSquare];
      
      if (piece) {
        if (piece.color === byColor && (piece.type === PieceType.Bishop || piece.type === PieceType.Queen)) {
          return true;
        }
        break; // Stop sliding if we hit any piece
      }
    }
  }

  // Check for rook/queen straight attacks
  for (const [rankDelta, fileDelta] of ROOK_DIRECTIONS) {
    let currentRank = rank;
    let currentFile = file;
    
    while (true) {
      currentRank += rankDelta;
      currentFile += fileDelta;
      
      if (currentRank < 0 || currentRank >= 8 || currentFile < 0 || currentFile >= 8) {
        break;
      }
      
      const targetSquare = currentRank * 8 + currentFile;
      const piece = state.board[targetSquare];
      
      if (piece) {
        if (piece.color === byColor && (piece.type === PieceType.Rook || piece.type === PieceType.Queen)) {
          return true;
        }
        break; // Stop sliding if we hit any piece
      }
    }
  }

  // Check for king attacks
  for (const [rankDelta, fileDelta] of KING_OFFSETS) {
    const kingRank = rank + rankDelta;
    const kingFile = file + fileDelta;
    
    if (kingRank >= 0 && kingRank < 8 && kingFile >= 0 && kingFile < 8) {
      const kingSquare = kingRank * 8 + kingFile;
      const piece = state.board[kingSquare];
      if (piece && piece.color === byColor && piece.type === PieceType.King) {
        return true;
      }
    }
  }

  return false;
}
